import { Alert, Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { deleteResult, getAllResults, restoreResult } from '@modules/firebase/results';
import { Result } from '@synergetx/puppet-interview-types';
import {
    useResultsColumns,
    useResultsColumnVisibility,
    RESULTS_INITIAL_SORT,
} from 'constants/resultsTableColumns';
import DownloadCSvButton from '@components/DownloadCsvButton';
import { log, logError } from '@modules/logging';
import DataGrid from '@components/DataGrid';
import DeletedResultsButton from '@components/DeletedResultsButton';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useInterviewOptions } from '@providers/InterviewsProvider';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

function Results() {
    const { selectedInterview } = useInterviewOptions();
    const groupId = selectedInterview?.groupId;
    const interviewId = selectedInterview?.id;

    const [results, setResults] = useState<Result[] | null>(null);
    const [deletedResults, setDeletedResults] = useState<Result[] | null>(null);
    const [showDeleted, setShowDeleted] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);

    const baseColumns = useResultsColumns(results ?? []);
    const columnVisibility = useResultsColumnVisibility(results ?? []);

    // Clear the rows during render when the selected survey changes (React recommended
    // pattern) so the grid shows its loading state rather than the previous survey's data.
    const surveyKey = `${groupId}:${interviewId}`;
    const [prevSurveyKey, setPrevSurveyKey] = useState(surveyKey);
    if (surveyKey !== prevSurveyKey) {
        setPrevSurveyKey(surveyKey);
        setResults(null);
        setDeletedResults(null);
        setLoadFailed(false);
    }

    // Bumped by the retry button to re-run the effect below.
    const [reloadToken, setReloadToken] = useState(0);

    useEffect(() => {
        if (!groupId || !interviewId) return;

        // Guard against a slow read for a previously selected survey resolving after the
        // user has already switched, and overwriting the newer survey's rows.
        let cancelled = false;

        (async () => {
            try {
                const allResults = await getAllResults(groupId, interviewId);
                if (cancelled) return;

                const newestFirst = [...allResults].sort((a, b) => b.updatedAt - a.updatedAt);

                setResults(newestFirst.filter(({ deleted }) => !deleted));
                setDeletedResults(newestFirst.filter(({ deleted }) => deleted));
                setLoadFailed(false);
            } catch (error) {
                if (cancelled) return;

                logError(error as Error);
                // `null` spins forever and `[]` alone reads as "no data" — flag the failure.
                setResults([]);
                setDeletedResults([]);
                setLoadFailed(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [groupId, interviewId, reloadToken]);

    const retryFetch = useCallback(() => {
        setResults(null);
        setLoadFailed(false);
        setReloadToken((token) => token + 1);
    }, []);

    const handleDelete = useCallback(async (data: Result) => {
        if (window.confirm(`Delete result: ${data.id || 'unknown'}, from: ${data.interviewer}? `)) {
            if (data.groupId && data.interviewId) {
                try {
                    const deletedResultItem = await deleteResult(data);
                    setResults((prev) =>
                        (prev ?? []).filter(({ id }) => id !== deletedResultItem.id),
                    );
                    setDeletedResults((prev) => [deletedResultItem].concat(prev ?? []));
                } catch (error) {
                    logError(error as Error);
                }
            } else {
                log(`Cannot delete result without group & interview`, { data });
                window.alert(
                    `Error deleting result. We are monitoring this issue and should get it fixed shortly.`,
                );
            }
        }
    }, []);

    const handleRestore = useCallback(async (data: Result) => {
        if (window.confirm(`Restore result: ${data.id || 'unknown'} from: ${data.interviewer}?`)) {
            if (data.groupId && data.interviewId) {
                try {
                    const restoredResult = await restoreResult(data);
                    setDeletedResults((prev) =>
                        (prev ?? []).filter(({ id }) => id !== restoredResult.id),
                    );
                    setResults((prev) => [restoredResult].concat(prev ?? []));
                } catch (error) {
                    logError(error as Error);
                }
            } else {
                log(`Cannot restore result without group & interview`, { data });
                window.alert(
                    `Error restoring result. We are monitoring this issue and should get it fixed shortly.`,
                );
            }
        }
    }, []);

    const actionsColumn: GridColDef<Result> = useMemo(
        () => ({
            field: 'actions',
            headerName: '',
            width: 60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            // A trash can read as "destroy this child's responses". Archiving is reversible — the
            // ARCHIVED tab restores it — so it gets the archive box, matching SurveySettingsModal.
            renderCell: (params: GridRenderCellParams<Result>) => {
                if (showDeleted) {
                    return (
                        <Tooltip title="Restore result">
                            <IconButton
                                size="small"
                                aria-label="Restore result"
                                onClick={() => handleRestore(params.row)}
                            >
                                <UnarchiveOutlinedIcon />
                            </IconButton>
                        </Tooltip>
                    );
                }
                if (params.row.deleted) return null;
                return (
                    <Tooltip title="Archive result">
                        <IconButton
                            size="small"
                            aria-label="Archive result"
                            onClick={() => handleDelete(params.row)}
                        >
                            <ArchiveOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                );
            },
        }),
        [showDeleted, handleDelete, handleRestore],
    );

    const columns = useMemo(() => [actionsColumn, ...baseColumns], [actionsColumn, baseColumns]);

    const displayData = showDeleted ? deletedResults : results;

    const emptyMessage = loadFailed ? (
        <Stack spacing={2} sx={{ alignItems: 'center' }}>
            <Alert severity="error" sx={{ textAlign: 'left' }}>
                We could not load results for this survey. This is usually a connection problem —
                your data has not been lost.
            </Alert>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={retryFetch}>
                Try again
            </Button>
        </Stack>
    ) : showDeleted ? (
        'No archived results'
    ) : (
        "No results yet, are you sure you've completed surveys?"
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0, // Allow flex shrinking
            }}
        >
            <DataGrid
                columns={columns}
                rows={displayData ?? []}
                loading={results == null}
                getRowId={(row) => row.id}
                columnVisibilityModel={columnVisibility}
                initialSortModel={RESULTS_INITIAL_SORT}
                toolbar={
                    <>
                        <Typography variant="h5" sx={{ mr: 2 }}>
                            View Results
                        </Typography>
                        <DeletedResultsButton
                            setShowDeleted={setShowDeleted}
                            showDeleted={showDeleted}
                        />
                        <DownloadCSvButton
                            haveResults={!!results?.length}
                            groupId={groupId}
                            interviewId={interviewId}
                        />
                    </>
                }
                emptyMessage={emptyMessage}
            />
        </Box>
    );
}

export default memo(Results);
